/**
 * "Before adding": everything to look at before the table is written, right above the button —
 * what blocks adding (with its fix), then what is worth a second look (with a way to show it).
 * Pure: the recipe and its preview in, edits and selections out.
 */

import type { TFunction } from 'i18next'
import { CircleAlert, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { MissingLink, OutputSummary, Preview, Proposal } from '@/services/api/structuredIngest'
import { LeftOutRowsNotice } from './LeftOutRows'
import { withEnabled, withLink, withSourceRowIdentity, withWindows } from './recipe-edits'
import { countPhrase, type ReviewState, type WindowOutput } from './review-state'
import { SchemaApproval } from './SchemaApproval'

interface Check {
  text: string
  target: string | null
  severity: 'warning' | 'destructive'
  action?: { label: string; onClick: () => void }
}

/**
 * Things worth a second look: what the preview could not build, the assistant's notes, identifiers,
 * and links the mapping leaves out. Sharing a row proves no link, so the analyst adds those.
 */
function collectChecks(
  { recipe, preview, summary, review }: ReviewPanelData,
  t: TFunction,
  onAddLink: (link: MissingLink) => void
): Check[] {
  const names = [...summary.nodes.map(n => n.name), ...summary.edges.map(e => e.name)]
  const blocking = new Set(review.validationErrors)
  if (preview.left_out?.blocking_error) blocking.add(preview.left_out.blocking_error)
  const numbersOnly = review.entities.flatMap(({ node, identity }) =>
    node.enabled && identity.kind === 'columns' && identity.numbersOnly
      ? [
          {
            text: t('tableMapping.review.numbersOnly', {
              name: node.name,
              example: identity.examples[0],
            }),
            target: node.name,
            severity: 'warning' as const,
          },
        ]
      : []
  )
  const missingLinks = (preview.missing_links ?? []).map(link => ({
    text: t('tableMapping.review.missingLink', {
      from: link.relationship.from,
      to: link.relationship.to,
      type: link.relationship.type,
      count: link.shared_rows,
    }),
    target: null,
    severity: 'warning' as const,
    action: { label: t('tableMapping.review.addRelationship'), onClick: () => onAddLink(link) },
  }))
  const candidates: Check[] = [
    ...(preview.warnings ?? []).map(w => ({
      text: w,
      target: names.find(n => w.includes(n)) ?? null,
      severity: 'destructive' as const,
    })),
    ...numbersOnly,
    ...missingLinks,
    ...(preview.coverage ?? []).map(w => ({ text: w, target: null, severity: 'warning' as const })),
    ...(recipe.warnings ?? []).map(w => ({ text: w, target: null, severity: 'warning' as const })),
  ]
  const seen = new Set<string>()
  return candidates.filter(item => {
    if (blocking.has(item.text) || seen.has(item.text)) return false
    seen.add(item.text)
    return true
  })
}

function validationTarget(error: string, recipe: Proposal): string | null {
  const names = [
    ...(recipe.subject_entities ?? []),
    ...(recipe.entity_descriptors ?? []),
    ...(recipe.relationships ?? []),
  ].map(item => item.name)
  return names.find(name => error.startsWith(`${name}:`)) ?? null
}

/** Which identity decision an error from the preview asks for, if any. */
function identityErrorKind(
  error: string
): 'identityProperty' | 'identityColumn' | 'nonUniqueProperty' | null {
  if (error.includes('not unique in the case schema')) return 'nonUniqueProperty'
  if (error.includes('choose an identity property')) return 'identityProperty'
  if (error.includes('identity column or value is required')) return 'identityColumn'
  return null
}

function validationDetail(error: string, target: string | null, t: TFunction) {
  const kind = identityErrorKind(error)
  if (target && kind) {
    return {
      title: t(`tableMapping.review.${kind}.title`, { name: target }),
      detail: t(`tableMapping.review.${kind}.detail`),
    }
  }
  return { title: t('tableMapping.review.cannotAdd'), detail: error }
}

function ValidationReview({
  data: { error, target, descriptor },
  disabled,
  actions,
}: {
  data: { error: string; target: string | null; descriptor: boolean }
  disabled: boolean
  actions: { onDisable: () => void; onInspect: () => void; onSourceRows: () => void }
}) {
  const { t } = useTranslation()
  const { title, detail } = validationDetail(error, target, t)
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border bg-background/60 p-3">
      <div className="min-w-0 max-w-3xl">
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{detail}</p>
      </div>
      {target && (
        <div className="flex flex-wrap gap-2 md:shrink-0">
          {descriptor && identityErrorKind(error) && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={actions.onSourceRows}
            >
              {t('tableMapping.review.keepPerRow')}
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={actions.onDisable}
          >
            {t('tableMapping.review.leaveOut', { name: target })}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={actions.onInspect}>
            {t('tableMapping.review.showMapping')}
          </Button>
        </div>
      )}
    </div>
  )
}

function NoOutputReview({
  windows,
  disabled,
  onMakeSearchable,
}: {
  windows: WindowOutput
  disabled: boolean
  onMakeSearchable: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-background/60 p-3">
      <p className="text-sm">
        {windows.enabled
          ? t('tableMapping.review.noOutput')
          : t('tableMapping.review.nothingSelected')}
      </p>
      {!windows.enabled && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={onMakeSearchable}
        >
          {t('tableMapping.review.makeSearchable')}
        </Button>
      )}
    </div>
  )
}

interface ReviewPanelData {
  recipe: Proposal
  preview: Preview
  summary: OutputSummary
  review: ReviewState
}

function Blocking({
  data,
  disabled,
  actions,
}: {
  data: ReviewPanelData
  disabled: boolean
  actions: { onChange: (recipe: Proposal) => void; onInspect: (name: string) => void }
}) {
  const { t } = useTranslation()
  const { recipe } = data
  const { schemaApprovals, validationErrors, blockedRows, noOutput, windows, blockers } =
    data.review
  if (blockers === 0) return null
  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-lg border border-warning-border bg-warning-surface p-3"
    >
      <p className="flex items-center gap-2 text-sm font-semibold text-warning-foreground">
        <CircleAlert aria-hidden className="size-4 shrink-0" />
        {t('tableMapping.review.blocking', { count: blockers })}
      </p>
      {schemaApprovals.length > 0 && (
        <div className="rounded-md border bg-background/60 p-3">
          <SchemaApproval recipe={recipe} disabled={disabled} onChange={actions.onChange} />
        </div>
      )}
      {blockedRows && (
        <div className="flex flex-col gap-2 rounded-md border bg-background/60 p-3">
          <p className="text-sm font-medium">{t('tableMapping.leftOut.blockingTitle')}</p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t('tableMapping.leftOut.blockingDetail')}
          </p>
          <LeftOutRowsNotice leftOut={blockedRows} />
        </div>
      )}
      {validationErrors.map(error => {
        const target = validationTarget(error, recipe)
        return (
          <ValidationReview
            key={error}
            data={{
              error,
              target,
              descriptor: !!recipe.entity_descriptors?.some(item => item.name === target),
            }}
            disabled={disabled}
            actions={{
              onDisable: () => target && actions.onChange(withEnabled(recipe, target, false)),
              onInspect: () => target && actions.onInspect(target),
              onSourceRows: () =>
                target &&
                actions.onChange(
                  withSourceRowIdentity(recipe, target, t('tableMapping.review.perRowNote'))
                ),
            }}
          />
        )
      })}
      {noOutput && (
        <NoOutputReview
          windows={windows}
          disabled={disabled}
          onMakeSearchable={() => actions.onChange(withWindows(recipe, true))}
        />
      )}
    </div>
  )
}

function SearchOnlyNotice({ windows }: { windows: WindowOutput }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-2.5 rounded-md border bg-muted/30 p-3 text-sm">
      <Search aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div>
        <p className="font-medium">{t('tableMapping.review.searchOnly')}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('tableMapping.review.searchOnlyDetail', {
            snippets: countPhrase(t, 'snippets', windows.count),
          })}
        </p>
      </div>
    </div>
  )
}

function Checks({
  items,
  disabled,
  onShow,
}: {
  items: Check[]
  disabled: boolean
  onShow: (id: string) => void
}) {
  const { t } = useTranslation()
  if (items.length === 0) return null
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map(c => (
        <li key={c.text} className="flex items-start gap-2 text-xs">
          <Badge
            variant={c.severity === 'warning' ? 'warning' : 'destructive'}
            className="mt-px shrink-0"
          >
            {c.severity === 'warning'
              ? t('tableMapping.review.check')
              : t('tableMapping.review.cannotBuild')}
          </Badge>
          <span className="min-w-0 flex-1">{c.text}</span>
          {c.action && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0"
              disabled={disabled}
              onClick={c.action.onClick}
            >
              {c.action.label}
            </Button>
          )}
          {c.target && (
            <button
              type="button"
              className="shrink-0 text-primary hover:underline max-md:relative max-md:after:absolute max-md:after:-inset-2.5"
              onClick={() => c.target && onShow(c.target)}
            >
              {t('tableMapping.review.show')}
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}

export function ReviewPanel({
  data,
  disabled,
  actions,
}: {
  data: ReviewPanelData
  disabled: boolean
  actions: { onChange: (recipe: Proposal) => void; onInspect: (name: string) => void }
}) {
  const { t } = useTranslation()
  const checks = collectChecks(data, t, link => actions.onChange(withLink(data.recipe, link)))
  const { blockers, searchOnly, windows } = data.review
  if (blockers === 0 && !searchOnly && checks.length === 0) return null
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-base font-semibold">{t('tableMapping.review.title')}</h3>
      <Blocking data={data} disabled={disabled} actions={actions} />
      {blockers === 0 && searchOnly && <SearchOnlyNotice windows={windows} />}
      <Checks items={checks} disabled={disabled} onShow={actions.onInspect} />
    </section>
  )
}
